// src/data/rosterOnFile.js
//
// RULING 330 PART 1 AND 2 - THE ROSTER ON FILE AND THE MATCH AT APPLICATION. Task list section 786.
//
// PURE. It says which rosters on file apply to an application, runs applicationDesk.rosterCrossReference against
// each, and words the result with the roster's STATUS, so an officer always sees whose word a match rests on.
// The callable decides what is stored; firestore.rules decides who reads it (no client write, ruling 330.1).
//
// THE SHAPE, from ruling 330 (Bob 2026-10-23 11:06 ET, "Except it"):
//   * a roster comes from the GC's onboarding item 5, an LC or S&T upload by link, or a PUBLIC carrier roster loaded
//     one by one with its source and date; each carries a status - confirmed (by the GC), provisional (public),
//     officer-supplied (LC or S&T by link, not yet confirmed by the GC).
//   * the desk states the status in words beside the match.
//   * a provisional or officer-supplied match NEVER grants access by itself (the wall, 2026-10-09), and before the
//     committee's letter is signed the best state an application reaches is VERIFIED - WAITING FOR THE COMMITTEE
//     (the 2026-10-13 rule, kept).
//   * ruling 194: never a block, and never a pass it did not make.
import { rosterCrossReference } from './applicationDesk';

const F = Object.freeze;

export const ROSTER_STATUS = F({
  confirmed: F({ rank: 3, words: 'confirmed by the General Chairperson' }),
  officer_supplied: F({ rank: 2, words: 'supplied by a local officer, not yet confirmed by the General Chairperson' }),
  provisional: F({ rank: 1, words: 'a public roster, not confirmed by the committee' }),
});
export const ROSTER_STATUSES = F(Object.keys(ROSTER_STATUS));
export const ROSTER_SOURCES = F(['gc_item5', 'officer_link', 'public']);

export const WAITING_FOR_COMMITTEE = 'VERIFIED - WAITING FOR THE COMMITTEE';
export const NOT_CHECKED = 'Not checked - no roster for this committee, craft and property is on file.';

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

// A roster as stored. Returns the list of problems; an empty list means it may be stored.
export function validateRoster(roster) {
  const r = roster || {};
  const out = [];
  ['committee', 'craft', 'property', 'source', 'date', 'loadedBy'].forEach((k) => {
    if (!String(r[k] || '').trim()) out.push(k + ' is required');
  });
  if (!ROSTER_STATUSES.includes(r.status)) out.push('status must be one of ' + ROSTER_STATUSES.join(', '));
  if (!ROSTER_SOURCES.includes(r.sourceKind)) out.push('sourceKind must be one of ' + ROSTER_SOURCES.join(', '));
  if (r.sourceKind === 'public' && r.status === 'officer_supplied') out.push('a public roster cannot be officer-supplied');
  if (r.sourceKind === 'officer_link' && r.status === 'provisional') out.push('an officer upload is officer-supplied, not provisional');
  if (r.status === 'confirmed' && !String(r.confirmedBy || '').trim()) out.push('a confirmed roster names who confirmed it');
  if (!Array.isArray(r.rows) || !r.rows.length) out.push('rows are required');
  else if (r.rows.some((x) => !x || !String(x.rosterNumber || '').trim())) out.push('every row carries a roster number');
  return out;
}

// The rosters on file that speak to this application: same committee, craft and property.
export function rostersFor(application, rosters) {
  const a = application || {};
  return (Array.isArray(rosters) ? rosters : []).filter((r) => r
    && norm(r.committee) === norm(a.generalCommittee)
    && norm(r.craft) === norm(a.craftAndClass)
    && norm(r.property) === norm(a.railroad)
    && validateRoster(r).length === 0);
}

const describe = (r) => {
  const d = r.date ? ' dated ' + r.date : '';
  if (r.status === 'confirmed') return 'the roster the General Chairperson confirmed' + d;
  if (r.status === 'officer_supplied') return 'the roster the ' + (r.loadedByOffice || 'local officer') + ' supplied' + d;
  return 'a public roster' + d + ', not confirmed by the committee';
};

// Runs the cross-reference against every roster that applies and returns the strongest result: a full match beats a
// partial one, and between equals the higher status wins. Never a block; NOT PERFORMED is never a pass.
export function matchOnFile(application, rosters) {
  const list = rostersFor(application, rosters);
  if (!list.length) {
    return F({ performed: false, status: '', statusWords: '', file: '', date: '', fields: F({}), match: null,
      summary: NOT_CHECKED, checked: 0 });
  }
  const results = list.map((r) => ({ r, x: rosterCrossReference(application, { file: r.source, date: r.date, rows: r.rows }) }));
  const score = ({ r, x }) => {
    const f = x.fields || {};
    const full = f.rosterNumber && f.rosterNumber.matched && f.seniorityDate && f.seniorityDate.matched ? 2
      : (f.rosterNumber && f.rosterNumber.matched ? 1 : 0);
    return full * 10 + ROSTER_STATUS[r.status].rank;
  };
  const best = results.slice().sort((p, q) => score(q) - score(p))[0];
  const { r, x } = best;
  const f = x.fields || {};
  const full = !!(f.rosterNumber && f.rosterNumber.matched && f.seniorityDate && f.seniorityDate.matched);
  const summary = full
    ? 'Matches ' + describe(r) + '.'
    : (f.rosterNumber && f.rosterNumber.matched
      ? 'Roster number ' + x.match.rosterNumber + ' is on ' + describe(r) + ', but its seniority date there is '
        + (x.match.seniorityDate || '(blank)') + '.'
      : 'Roster number ' + (String((application || {}).rosterNumber || '').trim() || '(none given)') + ' is not on '
        + describe(r) + (list.length > 1 ? ' or on any other roster on file' : '') + '.');
  return F({ performed: true, status: r.status, statusWords: ROSTER_STATUS[r.status].words, file: r.source,
    date: r.date, fields: x.fields, match: x.match, summary, checked: list.length });
}

// A match never grants access by itself, whatever its status (the wall, 2026-10-09). Access follows the Local
// Chairperson's decision (ruling 193) - this function exists so no caller ever reads a match as access.
export function grantsAccess() { return false; }

// The most an application may say about itself, given the match and whether the committee's letter is signed.
export function verificationState(result, letterSigned) {
  const x = result || {};
  const f = x.fields || {};
  const full = x.performed === true && f.rosterNumber && f.rosterNumber.matched && f.seniorityDate && f.seniorityDate.matched;
  if (!full) return x.performed === true ? 'NOT VERIFIED - FOR THE LOCAL CHAIRPERSON' : 'NOT CHECKED';
  if (!letterSigned) return WAITING_FOR_COMMITTEE;
  return x.status === 'confirmed' ? 'VERIFIED AGAINST THE CONFIRMED ROSTER' : 'MATCHED - ' + x.statusWords.toUpperCase();
}
